// ─────────────────────────────────────────────────────────────────────────────
// Backup Metadata Repository - IndexedDB Implementation
// Manages backup metadata including PDF URLs, image URLs, and sync status
// Links backup information to specific sedations and patients
// ─────────────────────────────────────────────────────────────────────────────

import { BaseEntity, DatabaseStore } from "../../types";
import { createRepo } from "./createRepo";

// Backup metadata interface extending BaseEntity
export interface BackupMetadataRecord extends BaseEntity {
  PatientId: string;
  SedationId: string;
  PatientName: string;
  BackupVersion: string;
  GeneratedAt: string;
  BackupType: 'complete' | 'partial' | 'emergency';
  
  // URLs for cloud-stored assets
  PdfUrl?: string | null;
  PatientPackUrl?: string | null;
  ImageUrls: string[]; // Array of image URLs
  DataBackupUrl?: string | null; // Complete data backup URL
  
  // Data statistics
  MeasurementsCount: number;
  ImagesCount: number;
  SessionDataSize: number;
  
  // Data integrity flags
  HasCompletePatientData: boolean;
  HasCompleteSedationDetail: boolean;
  HasIntraOpData: boolean;
  HasMeasurements: boolean;
  HasReport: boolean;
  
  // Sync status tracking
  SyncStatus: 'pending' | 'synced' | 'failed' | 'partial';
  SyncAttempts: number;
  LastSyncAttempt?: string | null;
  SyncError?: string | null;
  
  // Checksums for integrity verification
  Checksum: string;
}

// Create typed backup metadata repository instance
export const backupMetadataRepo = createRepo<BackupMetadataRecord>({
  storeName: DatabaseStore.BACKUP_METADATA,
  entityName: "BackupMetadata",
  skipOutbox: true, // Skip automatic outbox - only SedationCompletion should trigger sync
});

// Convenience methods for backup metadata operations

export const saveBackupMetadata = async (
  metadata: Omit<BackupMetadataRecord, "Id" | "CreatedDateTime">
): Promise<BackupMetadataRecord> => {
  return await backupMetadataRepo.create(metadata);
};

// Get backup metadata by patient ID
export const getBackupMetadataByPatientId = async (
  patientId: string
): Promise<BackupMetadataRecord[]> => {
  const allRecords = await backupMetadataRepo.listAll();
  return allRecords.filter(record => record.PatientId === patientId);
};

// Get backup metadata by sedation ID  
export const getBackupMetadataBySedationId = async (
  sedationId: string
): Promise<BackupMetadataRecord | null> => {
  const allRecords = await backupMetadataRepo.listAll();
  const record = allRecords.find(record => record.SedationId === sedationId) || null;
  return record;
};

// Update sync status
export const updateSyncStatus = async (
  id: string,
  status: 'pending' | 'synced' | 'failed' | 'partial',
  error?: string
): Promise<BackupMetadataRecord | null> => {
  const record = await backupMetadataRepo.getById(id);
  if (!record) return null;
  
  const updateData: Partial<BackupMetadataRecord> = {
    SyncStatus: status,
    SyncAttempts: record.SyncAttempts + 1,
    LastSyncAttempt: new Date().toISOString(),
    SyncError: error || null
  };
  
  return await backupMetadataRepo.update(id, updateData);
};

// Get pending sync records
export const getPendingSyncRecords = async (): Promise<BackupMetadataRecord[]> => {
  const allRecords = await backupMetadataRepo.listAll();
  return allRecords.filter(record => record.SyncStatus === 'pending' || record.SyncStatus === 'failed');
};

// Generate backup checksum from data
export const generateBackupChecksum = (data: any): string => {
  // Simple hash function for data integrity checking
  const jsonString = JSON.stringify(data);
  let hash = 0;
  for (let i = 0; i < jsonString.length; i++) {
    const char = jsonString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString();
};

// Verify backup integrity
export const verifyBackupIntegrity = async (
  id: string,
  currentData: any
): Promise<boolean> => {
  const record = await backupMetadataRepo.getById(id);
  if (!record) return false;
  
  const currentChecksum = generateBackupChecksum(currentData);
  return record.Checksum === currentChecksum;
};

// Get backup statistics
export const getBackupStatistics = async (): Promise<{
  totalBackups: number;
  syncedBackups: number;
  pendingBackups: number;
  failedBackups: number;
  backupsWithPdfs: number;
  backupsWithImages: number;
  totalImagesCount: number;
}> => {
  const allRecords = await backupMetadataRepo.listAll();
  
  return {
    totalBackups: allRecords.length,
    syncedBackups: allRecords.filter(r => r.SyncStatus === 'synced').length,
    pendingBackups: allRecords.filter(r => r.SyncStatus === 'pending').length,
    failedBackups: allRecords.filter(r => r.SyncStatus === 'failed').length,
    backupsWithPdfs: allRecords.filter(r => r.PdfUrl).length,
    backupsWithImages: allRecords.filter(r => r.ImagesCount > 0).length,
    totalImagesCount: allRecords.reduce((sum, r) => sum + r.ImagesCount, 0)
  };
};
